export const usePwaUpdate = () => {
  const updateAvailable = useState("updateAvailable", () => false)
  const updateError = useState("updateError", () => null)

  let registration = null
  let newServiceWorker = null
  let updateInterval = null

  const checkForUpdate = () => {
    if (registration?.waiting) {
      updateAvailable.value = true
      newServiceWorker = registration.waiting
    } else if (registration?.installing) {
      // Monitor installing service worker
      registration.installing.addEventListener("statechange", (event) => {
        if (
          event.target.state === "installed" &&
          navigator.serviceWorker.controller
        ) {
          updateAvailable.value = true
          newServiceWorker = event.target
        }
      })
    }
  }

  const updateApp = async () => {
    if (!newServiceWorker) {
      throw new Error("No service worker available to update")
    }

    try {
      // Create promise that resolves when controller changes
      const controllerChangePromise = new Promise((resolve, reject) => {
        const onControllerChange = () => {
          navigator.serviceWorker.removeEventListener(
            "controllerchange",
            onControllerChange
          )
          updateAvailable.value = false
          updateError.value = null
          window.location.reload()
          resolve()
        }

        navigator.serviceWorker.addEventListener(
          "controllerchange",
          onControllerChange
        )

        // Fallback timeout in case something goes wrong
        setTimeout(() => {
          navigator.serviceWorker.removeEventListener(
            "controllerchange",
            onControllerChange
          )
          reject(new Error("Update timeout"))
        }, 10000)
      })

      // Send skip waiting message
      newServiceWorker.postMessage({ type: "SKIP_WAITING" })

      // Wait for controller change
      await controllerChangePromise
    } catch (error) {
      updateError.value = error.message
      throw error
    }
  }

  const checkForUpdatesManually = async () => {
    if (!registration) {
      console.warn("No service worker registration available")
      return
    }

    try {
      await registration.update()
      checkForUpdate()
    } catch (error) {
      updateError.value = error.message
      console.error("Manual update check failed:", error)
      throw error
    }
  }

  const listenForUpdates = (event) => {
    if (event.type === "updatefound") {
      console.log("Service worker update found")
      checkForUpdate()
    }
  }

  const setupUpdateChecking = async () => {
    try {
      const reg = await navigator.serviceWorker.ready
      registration = reg

      // Set up event listener for updates
      registration.addEventListener("updatefound", listenForUpdates)

      // Check immediately in case there's already a waiting worker
      checkForUpdate()

      // Set up periodic update checks (every 30 minutes)
      updateInterval = setInterval(
        async () => {
          try {
            await checkForUpdatesManually()
          } catch (error) {
            console.error("Periodic update check failed:", error)
          }
        },
        30 * 60 * 1000
      )
    } catch (error) {
      console.error("Failed to set up service worker update checking:", error)
      updateError.value = error.message
    }
  }

  const setupControllerChangeListener = () => {
    // Listen for controller changes (new SW activated)
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      console.log("Service worker controller changed")
      // Only reload if we're not in the middle of updating
      if (!updateAvailable.value) {
        window.location.reload()
      }
    })
  }

  const cleanup = () => {
    if (registration) {
      registration.removeEventListener("updatefound", listenForUpdates)
    }

    if (updateInterval) {
      clearInterval(updateInterval)
      updateInterval = null
    }
  }

  onMounted(async () => {
    if ("serviceWorker" in navigator) {
      await setupUpdateChecking()
      setupControllerChangeListener()
    }
  })

  onUnmounted(() => {
    cleanup()
  })

  return {
    updateAvailable: readonly(updateAvailable),
    updateError: readonly(updateError),
    updateApp,
    checkForUpdatesManually,
  }
}
